import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';

import { experimentalStyled as styled } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import MuiDrawer from '@material-ui/core/Drawer';
import Box from '@material-ui/core/Box';
import MuiAppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Container from '@material-ui/core/Container';
import LogoutIcon from '@material-ui/icons/Logout';

import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

import MenuList from './_menuList';

const drawerWidth = 280;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (property) => property !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
    },
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (property) => property !== 'open',
})(({ theme, open }) => ({
  '&': { zIndex: 100, height: '100%' },
  '& .MuiDrawer-paper': {
    borderRight: 'none',
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',

    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: 0,
      [theme.breakpoints.up('md')]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

const NavMenu = ({ children, show, onLogout, onToggle, title }) => {
  const history = useHistory();

  const appBar = useRef();

  const [open, setOpen] = useState(false);
  const [isMobileWidth, setIsMobileWidth] = useState(false);
  const [appBarHeight, setAppBarHeight] = useState(0);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  useEffect(() => {
    onToggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateSize = () => {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );

    if (!isMobileWidth && vw < 960) {
      setIsMobileWidth(true);
    } else setIsMobileWidth(false);

    setAppBarHeight(appBar && appBar.current ? appBar.current.offsetHeight : 0);
  };

  useEffect(() => {
    setAppBarHeight(appBar && appBar.current ? appBar.current.offsetHeight : 0);
  }, [appBar]);

  useEffect(() => {
    if (isMobileWidth) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobileWidth]);

  useEffect(() => {
    window.addEventListener('resize', updateSize);
    updateSize();

    history.listen(() => {
      updateSize();
    });
    return () => window.removeEventListener('resize', updateSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={show ? { display: 'flex' } : {}}>
      <CssBaseline />
      <AppBar
        position="absolute"
        open={open}
        ref={appBar}
        sx={{ zIndex: show ? 1201 : -1000, opacity: show ? 1 : 0 }}
      >
        <Toolbar sx={{ pr: '16px' }}>
          {show ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: '36px',
                ...(open && { display: { md: 'none' } }),
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : undefined}

          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {title}
          </Typography>

          {show ? (
            <IconButton color="inherit" onClick={() => onLogout()}>
              <LogoutIcon />
            </IconButton>
          ) : undefined}
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          position: {
            xs: 'fixed',
            md: 'relative',
            display: show ? 'block' : 'none',
          },
          boxShadow: {
            xs: '0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)',
            md: 'none',
          },
          WebkitBoxShadow: {
            xs: '0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)',
            md: 'none',
          },
        }}
        variant="permanent"
        open={open}
      >
        <Toolbar
          className="toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: [1],
          }}
        >
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <MenuList isMobileWidth={isMobileWidth} setOpen={setOpen} />
      </Drawer>

      <Box
        component="main"
        sx={
          show
            ? {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[900],
                flexGrow: 1,
                height: '100vh',
                overflow: 'auto',
              }
            : {}
        }
      >
        <Container
          maxWidth={show ? 'lg' : ''}
          sx={
            show
              ? {
                  minHeight: '100vh',
                  paddingTop: `${appBarHeight}px`,
                }
              : {}
          }
        >
          <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>{children}</Box>
        </Container>
      </Box>
    </Box>
  );
};

NavMenu.propTypes = {
  children: PropTypes.node,
  show: PropTypes.bool,
  onLogout: PropTypes.func,
  onToggle: PropTypes.func,
  title: PropTypes.string,
};

NavMenu.defaultProps = {
  onLogout: () => {},
  onToggle: () => {},
};

export default NavMenu;
